// ============================================================================
// AccountUserManage.jsx
// -----------------------------------------------------------------------------
// Composant modal de création / édition d’un utilisateur de compte
// Intègre le template modal générique (ModalTemplate) pour uniformiser les fenêtres
// ============================================================================

import React, { useState, useEffect } from "react";
import theme from "../../../styles/theme";
import Input from "../../reusable/Forms/Input";
import ModalTemplate from "../../templates/ModalTemplate";
import DropdownList from "../../reusable/Forms/DropdownList";
import { USER_ROLES, USER_STATUS } from "../../../base/constants";
import { API_ROUTES } from "../../../config/api";
import{apiFetch} from "../../../config/apiFetch";

// ============================================================================
// 🔸 Composant principal
// ============================================================================
const AccountUserManage = ({
  isOpen,
  onClose,
  onSave,
  mode = "create",
  initialValues = null,
}) => {
  // --------------------------------------------------------------------------
  // 🧩 1. État initial du formulaire
  // --------------------------------------------------------------------------
  const initialState = {
    id: null,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    role: "Member",
    status: "Active",
    accountName: "",
  };
  const [formData, setFormData] = useState(initialState);

  // --------------------------------------------------------------------------
// 🔹 2. Initialisation du formulaire selon le mode (création / édition)
// --------------------------------------------------------------------------
useEffect(() => {
  if (!isOpen) return;

    // normalisation robuste sur les constantes
    const normalize = (value, reference) => {
      const match = Object.values(reference).find(
        (v) => v.toLowerCase() === String(value ?? "").toLowerCase()
      );
      return match || value || "";
    };

    const v = initialValues ?? {};
    setFormData({
      id: v.id ?? null,
      firstName: v.firstName ?? "",
      lastName: v.lastName ?? "",
      email: v.email ?? "",
      phone: v.phone ?? "",
      companyName: v.companyName ?? "",
      accountName: v.accountName ?? "",  
      role: normalize(v.role ?? "Member", USER_ROLES),      // ✅ important
      status: normalize(v.status ?? "Active", USER_STATUS), // ✅ important
    });
  }, [isOpen, initialValues]);

  // --------------------------------------------------------------------------
  // 🔹 3. Gestion des changements dans les champs
  // --------------------------------------------------------------------------
  const handleChange = (field) => (e) =>
    setFormData({ ...formData, [field]: e.target.value });

  // --------------------------------------------------------------------------
  // 🔹 4. Soumission du formulaire vers l’API
  // --------------------------------------------------------------------------
 const handleSubmit = async () => {
  try {
    const isEdit = mode === "edit";
    const id = formData.id;

    // 🔹 Normalisation stricte vers les constantes officielles
    const normalize = (value, reference) => {
      if (!value) return null;
      const key = Object.keys(reference).find(
        (k) => reference[k].toLowerCase() === value.toLowerCase()
      );
      return reference[key] || value;
    };

    // ✅ On envoie à l’API les clés cohérentes
    const payload = {
      id: id || 0,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: formData.phone?.trim() || null,
      companyName: formData.companyName.trim(),
      accountName: formData.accountName.trim(),
      role: normalize(formData.role, USER_ROLES),
      status: normalize(formData.status, USER_STATUS),
    };

    const url = isEdit
  ? `${API_ROUTES.users.base}/${id}`
  : API_ROUTES.users.base;


    const method = isEdit ? "PUT" : "POST";

    const res = await apiFetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const msg = await res.text();
      throw new Error(`HTTP ${res.status}: ${msg}`);
    }

    const data = await res.json();
    const user = data?.user ?? data;

    // 🔹 Réapplique les constantes locales
    user.role = normalize(user.role, USER_ROLES);
    user.status = normalize(user.status, USER_STATUS);

    onSave(user);
    onClose();
  } catch (err) {
    console.error("Erreur soumission :", err);
    alert(err.message);
  }
};



  // --------------------------------------------------------------------------
  // 🔹 5. Styles internes des champs
  // --------------------------------------------------------------------------
  const labelStyle = {
    display: "block",
    marginBottom: theme.spacing.xs,
  };
  const fieldStyle = {
    width: "100%",
    fontSize: theme.form.field.fontSize,
    padding: `${theme.form.field.paddingV}px ${theme.form.field.paddingH}px`,
  };

  // --------------------------------------------------------------------------
  // 🔹 5. Palettes de couleurs
  // --------------------------------------------------------------------------
  // Palette de couleurs spécifiques depuis le thème
  const userRoleColors = theme.statusColors?.userRole;
  const userStatusColors = theme.statusColors?.userStatus;

  // --------------------------------------------------------------------------
  // 🔹 6. Rendu JSX via ModalTemplate
  // --------------------------------------------------------------------------
  return (
    <ModalTemplate
      isOpen={isOpen}
      title={mode === "edit" ? "Éditer l’utilisateur" : "Créer un utilisateur"}
      onClose={onClose}
      onSubmit={handleSubmit}
      width="400px"
    >
      {/* -------------------------------------------------------------- */}
      {/* Champs du formulaire */}
      {/* -------------------------------------------------------------- */}
      {mode === "edit" && (
        <Input
          label={<span style={labelStyle}>ID</span>}
          value={formData.id}
          onChange={handleChange("id")}
          disabled
        />
      )}
      <Input
        label={<span style={labelStyle}>Nom du compte</span>}
        value={formData.accountName}
        onChange={handleChange("accountName")}
      />

      <Input
        label={<span style={labelStyle}>Prénom</span>}
        value={formData.firstName}
        onChange={handleChange("firstName")}
      />
      <Input
        label={<span style={labelStyle}>Nom</span>}
        value={formData.lastName}
        onChange={handleChange("lastName")}
      />
      <Input
        label={<span style={labelStyle}>Email</span>}
        value={formData.email}
        onChange={handleChange("email")}
      />
      <Input
        label={<span style={labelStyle}>Téléphone</span>}
        value={formData.phone}
        onChange={handleChange("phone")}
      />
      <Input
        label={<span style={labelStyle}>Entreprise</span>}
        value={formData.companyName}
        onChange={handleChange("companyName")}
      />

      {/* -------------------------------------------------------------- */}
      {/* Sélecteur de rôle */}
      {/* -------------------------------------------------------------- */}
      <div style={{ width: "100%" }}>
        <DropdownList
          label={<span style={labelStyle}>Rôle</span>}
          value={formData.role}
          onChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
          options={Object.values(USER_ROLES).map((role) => ({
            value: role,
            label: role,
          }))}
          placeholder="Sélectionner un rôle..."
          colorCategory={userRoleColors} // ✅ lien avec palette du thème
        />
      </div>


      {/* -------------------------------------------------------------- */}
      {/* Sélecteur de statut */}
      {/* -------------------------------------------------------------- */}
      <div style={{ width: "100%" }}>
        <DropdownList
          label={<span style={labelStyle}>Statut</span>}
          value={formData.status}
          onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
          options={Object.values(USER_STATUS).map((status) => ({
            value: status,
            label: status,
          }))}
          placeholder="Sélectionner un statut..."
          colorCategory={userStatusColors} // ✅ lien avec palette du thème
        />
      </div>

    </ModalTemplate>
  );
};

// ============================================================================
export default AccountUserManage;
